<template>
  <div class="seat-step">
    <div class="seat-glyph" aria-hidden="true">✦</div>
    <h2 class="seat-title">{{ readerName }} is ready</h2>
    <p class="seat-body">
      Open your Studio and let seekers find them.
    </p>

    <div v-if="loading" class="seat-loading">Reading the stars…</div>

    <div v-else-if="offer" class="seat-offer">
      <div class="seat-price">
        <span class="seat-amount">{{ formatAmount(offer.intro_carats) }}</span>
        <span class="seat-term">for your first {{ offer.intro_months }} months</span>
      </div>
      <p class="seat-then">
        Then {{ formatAmount(offer.monthly_carats) }} a month, at the rate you
        join on. It stays yours as the Community grows.
      </p>

      <button
        class="seat-go"
        :disabled="subscribing || !offer.affordable"
        @click="openSeat"
      >
        {{ subscribing ? 'Opening…' : 'Open my Studio' }}
      </button>

      <!-- The price as a goal, not a shortfall (quest journey 2026-10-27). -->
      <div v-if="!offer.affordable" class="seat-goal" data-testid="seat-goal">
        <div class="seat-goal-bar" aria-hidden="true"><span :style="{ width: goalPct + '%' }"></span></div>
        <p class="seat-hint">{{ goalLine }}</p>
      </div>
      <p v-if="error" class="seat-hint seat-error">{{ error }}</p>
    </div>

    <div v-else class="seat-hint">Seats are not open right now.</div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { store } from '@/store'
import { getMysticSeat, subscribeMysticSeat } from '@/api-service'
import { formatAmount } from '@/utils/carats'
import { seatGoalLine } from '@/utils/studio-unlock'

const props = defineProps({
  readerName: { type: String, default: 'Your Mystic' },
})
const emit = defineEmits(['seat-opened'])

const loading = ref(true)
const subscribing = ref(false)
const offer = ref(null)
const balance = ref(0)
const error = ref('')
// "1,240 of 2,000 ✦ · keep showing up, or add carats"
const goalLine = computed(() => seatGoalLine(balance.value, offer.value && offer.value.intro_carats))
const goalPct = computed(() => { const p = Number(offer.value && offer.value.intro_carats) || 0; return p ? Math.min(100, Math.round((Number(balance.value) || 0) / p * 100)) : 0 })

onMounted(async () => {
  try {
    const d = await getMysticSeat(store.session_id)
    offer.value = d?.offer || null
    balance.value = d?.balance || 0
    // Already seated — nothing to sell, go straight through.
    if (d?.seat?.active) emit('seat-opened')
  } catch (e) {
    error.value = 'Could not reach the gate. Try again in a moment.'
  } finally {
    loading.value = false
  }
})

async function openSeat() {
  if (subscribing.value) return
  subscribing.value = true
  error.value = ''
  try {
    const r = await subscribeMysticSeat(store.session_id, offer.value?.plan)
    if (r?.ok) {
      if (typeof r.balance === 'number') store.querent_carat_balance = r.balance
      emit('seat-opened')
    } else {
      error.value = r?.reason === 'insufficient'
        ? 'Not quite enough carats yet.'
        : 'That did not go through. Try again.'
    }
  } catch (e) {
    error.value = 'That did not go through. Try again.'
  } finally {
    subscribing.value = false
  }
}
</script>

<style scoped>
/* Same tokens as the rest of the app's modals — #14071f ground, gold Cinzel
   titles, violet pill for the action. */
.seat-step {
  max-width: 420px;
  margin: 0 auto;
  padding: 32px 22px;
  text-align: center;
  color: #e9dcff;
}
.seat-glyph { font-size: 34px; color: #f0d9a3; margin-bottom: 10px; }
.seat-title { font-family: 'Cinzel', serif; font-size: 22px; color: #f5e8c0; margin: 0 0 8px; }
.seat-body { font-size: 14px; color: #c9b8e8; margin: 0 0 22px; line-height: 1.55; }
.seat-loading { font-size: 13px; color: #9b86c9; }
.seat-offer {
  border: 1px solid rgba(240, 217, 163, 0.22);
  border-radius: 16px;
  padding: 22px 18px;
  background: rgba(255, 255, 255, 0.03);
}
.seat-price { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }
.seat-amount { font-family: 'Cinzel', serif; font-size: 26px; color: #f5e8c0; }
.seat-term { font-size: 12.5px; color: #9b86c9; }
.seat-then { font-size: 12.5px; color: #c9b8e8; margin: 0 0 18px; line-height: 1.5; }
.seat-go {
  background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #fff; border: none; border-radius: 999px;
  padding: 11px 30px; font-size: 14px; font-weight: 700;
  font-family: inherit; cursor: pointer;
}
.seat-go:hover:not(:disabled) { filter: brightness(1.12); }
.seat-go:disabled { opacity: 0.5; cursor: default; }
.seat-hint { margin: 12px 0 0; font-size: 12px; color: #9b86c9; line-height: 1.5; }
.seat-error { color: #f0a3a3; }
.seat-goal { margin-top: 10px; }
.seat-goal-bar { height: 4px; border-radius: 99px; background: rgba(155,134,201,.25); overflow: hidden; max-width: 240px; margin: 0 auto 6px; }
.seat-goal-bar span { display: block; height: 100%; background: linear-gradient(90deg, #c9a94e, #f0d9a3); }
</style>
